import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { Button, EmptyState, ErrorState, MetricTooltip, Tabs } from '@bb/ui';
import { ApiError, type CanvasAnalytics, type CanvasAnalyticsQuery, type CanvasDefinition } from '../../../api/client.ts';
import { PeriodPicker } from '../../analytics/PeriodPicker.tsx';
import { lastDays, periodProblem, periodSearch, type Period } from '../../analytics/period.ts';
import { PushPerformancePanel } from '../../analytics/push/PushPerformancePanel.tsx';
import { ActivityLog } from '../../analytics/activity/ActivityLog.tsx';
import { FunnelReportPanel } from '../../analytics/reports/FunnelReportPanel.tsx';
import { RetentionReportPanel } from '../../analytics/reports/RetentionReportPanel.tsx';
import { VariantStatsPanel } from '../../analytics/variants/VariantStatsPanel.tsx';
import { CanvasStatusBadge } from '../CanvasStatusBadge.tsx';
import { StepStatsOverlay } from './StepStatsOverlay.tsx';
import { StepStatsTable } from './StepStatsTable.tsx';
import { StuckEntriesTable } from './StuckEntriesTable.tsx';
import { orderSteps, sendsPush } from './labels.ts';

export interface CanvasAnalyticsSearch {
  from?: string;
  to?: string;
  versionId?: string;
  variantId?: string;
  view?: 'graph' | 'table';
  /** The Message step (`stepId`) whose push performance is shown instead of the whole Canvas. */
  pushStep?: string;
  /** The open tab; the overview by default. */
  tab?: CanvasAnalyticsTab;
}

export const CANVAS_ANALYTICS_TABS = ['overview', 'variants', 'funnel', 'retention', 'activity'] as const;
export type CanvasAnalyticsTab = (typeof CANVAS_ANALYTICS_TABS)[number];
const CANVAS_TAB_LABELS: Record<CanvasAnalyticsTab, string> = { overview: 'Overview', variants: 'Variants and control', funnel: 'Funnel', retention: 'Retention', activity: 'Message activity' };

/** `?from&to&versionId&variantId&view&pushStep&tab` of the Canvas analytics page; anything else is dropped. */
export const canvasAnalyticsSearch = (search: Record<string, unknown>): CanvasAnalyticsSearch => ({
  ...periodSearch(search),
  ...(typeof search.versionId === 'string' && search.versionId ? { versionId: search.versionId } : {}),
  ...(typeof search.variantId === 'string' && search.variantId ? { variantId: search.variantId } : {}),
  ...(search.view === 'table' ? { view: 'table' as const } : {}),
  ...(typeof search.pushStep === 'string' && search.pushStep ? { pushStep: search.pushStep } : {}),
  ...(typeof search.tab === 'string' && search.tab !== 'overview' && (CANVAS_ANALYTICS_TABS as readonly string[]).includes(search.tab) ? { tab: search.tab as CanvasAnalyticsTab } : {}),
});

/** KPI definitions as the service counts them (`canvas-analytics.ts`, docs/features/canvas-analytics.md). */
const KPIS: Array<{ key: keyof CanvasAnalytics['kpis']; title: string; formula: string; expression: string }> = [
  { key: 'entries', title: 'Entries', formula: 'Entries into the Canvas in the period, control group included.', expression: 'count(entry where entered_at in period)' },
  { key: 'completed', title: 'Completed', formula: 'Entries that reached the end of their path in the period.', expression: "count(entry where status = 'completed' and finished_at in period)" },
  { key: 'exited', title: 'Exited', formula: 'Entries that left early in the period: exit criteria, exit audience, a suppressed message, the control group.', expression: "count(entry where status = 'exited' and finished_at in period)" },
  { key: 'failed', title: 'Failed', formula: 'Entries that stopped on an error in the period.', expression: "count(entry where status = 'failed' and finished_at in period)" },
  { key: 'waiting', title: 'Waiting now', formula: 'Entries waiting in a step right now, whatever the period.', expression: "count(entry where status = 'waiting')" },
];


const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/**
 * Canvas → Analytics (BB-123): KPIs, numbers by step on the graph or in a table (entered, waiting, advanced by port,
 * exited by reason, failed, sends, time in step), stuck entries and push performance of the Canvas or of one of its
 * Message steps, for a period, version and variant kept in the URL. Tabs add variants against control (BB-136) and
 * the funnel and retention reports (BB-135) and the Message Activity Log of the Canvas (BB-105).
 */
export function CanvasAnalyticsPage() {
  const from = '/env/$environmentId/canvas/$canvasId/analytics';
  const { environmentId, canvasId } = useParams({ from });
  const { api } = useRouteContext({ from });
  const search = useSearch({ from });
  const navigate = useNavigate({ from });
  const canvas = useQuery({ queryKey: ['canvases', environmentId, 'one', canvasId], queryFn: () => api.getCanvas(environmentId, canvasId) });
  const draft = useQuery({ queryKey: ['canvases', environmentId, 'draft', canvasId], queryFn: () => api.canvasDraft(environmentId, canvasId), staleTime: Infinity });
  const types = useQuery({ queryKey: ['canvas-step-types', environmentId], queryFn: () => api.canvasStepTypes(environmentId), staleTime: Infinity });
  const period: Period = search.from && search.to ? { from: search.from, to: search.to } : lastDays(30);
  const valid = periodProblem(period) === null;
  const launched = Boolean(canvas.data && (canvas.data.publishedVersion !== null || canvas.data.versions.length > 0));
  const query: CanvasAnalyticsQuery = { from: period.from, to: period.to, ...(search.versionId ? { versionId: search.versionId } : {}), ...(search.variantId ? { variantId: search.variantId } : {}) };
  const analytics = useQuery({ queryKey: ['canvases', environmentId, 'analytics', canvasId, query], queryFn: () => api.canvasAnalytics(environmentId, canvasId, query), enabled: valid && launched, placeholderData: keepPreviousData });
  // Filters change the URL of the same page: the page stays where it is scrolled.
  const go = (patch: Partial<CanvasAnalyticsSearch>) => navigate({ search: (prev: CanvasAnalyticsSearch) => canvasAnalyticsSearch({ ...prev, ...patch }), replace: true, resetScroll: false });
  const set = (patch: Partial<CanvasAnalyticsSearch>) => void go(patch);
  const pushPanel = useRef<HTMLDivElement>(null);

  // The running version's graph with the draft's positions; before the first launch, the draft.
  const graph = useMemo<Pick<CanvasDefinition, 'variants' | 'steps' | 'layout'> | null>(() => {
    if (!draft.data) return null;
    const running = draft.data.published?.definition;
    return running ? { variants: running.variants, steps: running.steps, ...(draft.data.definition.layout ? { layout: draft.data.definition.layout } : {}) } : draft.data.definition;
  }, [draft.data]);
  const typeItems = types.data?.items;
  const typeLabel = useCallback((type: string) => typeItems?.find((t) => t.type === type)?.label ?? (type === 'unknown' ? 'Unknown' : type), [typeItems]);

  if (canvas.isPending) return <p aria-busy="true">Loading Canvas…</p>;
  if (canvas.isError) return <ErrorState title="Canvas could not be loaded" message={errorText(canvas.error)} onRetry={() => void canvas.refetch()} />;
  const c = canvas.data;
  const versions = c.versions;
  const variantNames = new Map((graph?.variants ?? []).map((v) => [v.id, v.name]));
  const variantIds = [...new Set([...variantNames.keys(), ...(analytics.data?.variants.map((v) => v.variantId) ?? []), ...(search.variantId ? [search.variantId] : [])])];
  const data = analytics.data;
  const sends = data ? data.steps.reduce((acc, s) => acc + s.sends, 0) : 0;
  const active = data ? Object.values(data.kpis).some(Boolean) || data.steps.some((s) => s.entered || s.waiting || s.sends) : false;
  const offGraph = data && graph ? data.steps.filter((s) => !graph.steps[s.stepId]).length : 0;
  const view = search.view ?? 'graph';
  // Steps of the graph, then those only older versions had (their names come with the numbers).
  const graphSteps = graph ? Object.values(graph.steps).map((s) => ({ id: s.id, name: s.name })) : c.steps.map((s) => ({ id: s.id, name: s.name }));
  const stepNames = [...graphSteps, ...(data?.steps.filter((s) => !graphSteps.some((g) => g.id === s.stepId)).map((s) => ({ id: s.stepId, name: s.name })) ?? [])];
  // Push performance of one Message step: push analytics filtered by its `stepUuid` (the facts' `canvasStepId`).
  const pushSteps = data ? orderSteps(graph, data.steps).filter((s) => sendsPush(graph, s)) : [];
  const pushStep = search.pushStep ? pushSteps.find((s) => s.stepId === search.pushStep) : undefined;
  const pushStepPending = Boolean(search.pushStep) && !data && !analytics.isError;
  const openPush = (stepId: string) => void go({ pushStep: stepId }).then(() => pushPanel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));

  const overview = (
    <>
      <div className="filters" role="group" aria-label="Filters">
        <PeriodPicker value={period} onChange={(p) => set({ from: p.from, to: p.to })} />
        <label title={versions.length ? undefined : 'Filtering by version needs version ids, which the Canvas API does not return yet.'}>
          Version{' '}
          <select value={search.versionId ?? ''} disabled={versions.length === 0 && !search.versionId} onChange={(e) => set({ versionId: e.target.value || undefined })}>
            <option value="">All versions</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                Version {v.version}
              </option>
            ))}
            {search.versionId && !versions.some((v) => v.id === search.versionId) && <option value={search.versionId}>Version {search.versionId}</option>}
          </select>
        </label>
        <label>
          Variant{' '}
          <select value={search.variantId ?? ''} onChange={(e) => set({ variantId: e.target.value || undefined })}>
            <option value="">All variants</option>
            {variantIds.map((id) => (
              <option key={id} value={id}>
                {variantNames.get(id) ?? id}
              </option>
            ))}
          </select>
        </label>
      </div>
      {analytics.isError ? (
        <ErrorState title="Analytics could not be loaded" message={errorText(analytics.error)} {...(analytics.error instanceof ApiError ? { code: analytics.error.code } : {})} onRetry={() => void analytics.refetch()} />
      ) : !data ? (
        valid && <p aria-busy="true">Loading analytics…</p>
      ) : (
        <>
          <p className="hint">
            {data.from} – {data.to}, whole days in the environment time zone. Waiting counts are live. Numbers are counted from the journey log when the page loads.
          </p>
          <ul aria-label="Key metrics" className="kpi-grid">
            {KPIS.map((k) => (
              <li key={k.key} className="kpi-grid__tile">
                <span className="kpi-grid__title">{k.title}</span>
                <MetricTooltip title={k.title} value={data.kpis[k.key].toLocaleString('en-US')} formula={k.formula} expression={k.expression} />
              </li>
            ))}
            <li className="kpi-grid__tile">
              <span className="kpi-grid__title">Sends</span>
              <MetricTooltip title="Sends" value={sends.toLocaleString('en-US')} formula="Messages Message steps handed to a channel in the period (test sends excluded)." expression="sum(step.sends)" {...(search.variantId ? { explanation: 'Sends are counted per step, not per entry, so the variant filter does not narrow them.' } : {})} />
            </li>
          </ul>
          {!active ? (
            <EmptyState
              title="No activity in this period"
              description="Nobody entered, moved or waited in this Canvas in the chosen days."
              action={
                <button type="button" className="button" onClick={() => set(lastDays(90))}>
                  Show the last 90 days
                </button>
              }
            />
          ) : (
            <>
              {data.variants.length > 0 && (
                <table className="table variant-stats" aria-label="Entries by variant">
                  <thead>
                    <tr>
                      <th scope="col">Variant</th>
                      <th scope="col">Entries</th>
                      <th scope="col">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.variants.map((v) => (
                      <tr key={v.variantId}>
                        <th scope="row">{variantNames.get(v.variantId) ?? v.variantId}</th>
                        <td>{v.entries.toLocaleString('en-US')}</td>
                        <td>{data.kpis.entries ? `${((v.entries / data.kpis.entries) * 100).toFixed(1)} %` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="canvas-analytics__steps-head">
                <h2>Steps</h2>
                <div className="toolbar" role="group" aria-label="Steps view">
                  <button type="button" className="button" aria-pressed={view === 'graph'} onClick={() => set({ view: undefined })} disabled={!graph}>
                    Graph
                  </button>
                  <button type="button" className="button" aria-pressed={view === 'table'} onClick={() => set({ view: 'table' })}>
                    Table
                  </button>
                </div>
              </div>
              {view === 'graph' && graph ? (
                <>
                  <StepStatsOverlay graph={graph} analytics={data} typeLabel={typeLabel} />
                  {offGraph > 0 && <p className="hint">{offGraph === 1 ? '1 step with numbers is' : `${offGraph} steps with numbers are`} not in the graph (removed in a later version): see the table.</p>}
                </>
              ) : (
                <StepStatsTable steps={data.steps} graph={graph} typeLabel={typeLabel} push={{ stepIds: new Set(pushSteps.map((s) => s.stepId)), open: openPush }} />
              )}
            </>
          )}
        </>
      )}
      <StuckEntriesTable environmentId={environmentId} canvasId={canvasId} steps={stepNames} />
      <div ref={pushPanel}>
        {pushStepPending ? (
          <p aria-busy="true">Loading push performance…</p>
        ) : (
          <PushPerformancePanel
            environmentId={environmentId}
            scope={pushStep ? { canvasId, canvasStepId: pushStep.stepUuid } : { canvasId }}
            period={period}
            {...(variantNames.size ? { variantLabels: Object.fromEntries(variantNames) } : {})}
            toolbarExtra={
              pushSteps.length > 0 && (
                <label>
                  Step{' '}
                  <select value={pushStep?.stepId ?? ''} onChange={(e) => set({ pushStep: e.target.value || undefined })}>
                    <option value="">Whole Canvas</option>
                    {pushSteps.map((s) => (
                      <option key={s.stepId} value={s.stepId}>
                        {s.name}
                        {s.inGraph ? '' : ' (not in the current graph)'}
                      </option>
                    ))}
                  </select>
                </label>
              )
            }
            note={
              pushStep ? (
                <p className="hint">
                  Pushes sent by the Message step «{pushStep.name}» only.{' '}
                  <Button size="sm" variant="ghost" onClick={() => set({ pushStep: undefined })}>
                    Show the whole Canvas
                  </Button>
                </p>
              ) : search.pushStep && data ? (
                <p className="hint">This Canvas has no Message step «{search.pushStep}» that sends push: showing the whole Canvas.</p>
              ) : null
            }
          />
        )}
      </div>
    </>
  );
  const tab: CanvasAnalyticsTab = search.tab ?? 'overview';
  const tabs: Array<{ value: CanvasAnalyticsTab; content: ReactNode }> = [
    { value: 'overview', content: overview },
    { value: 'variants', content: <VariantStatsPanel environmentId={environmentId} object={{ type: 'canvas', id: canvasId }} period={period} /> },
    { value: 'funnel', content: <FunnelReportPanel environmentId={environmentId} source={{ canvasId }} period={period} onPeriodChange={(p) => set({ from: p.from, to: p.to })} /> },
    { value: 'retention', content: <RetentionReportPanel environmentId={environmentId} source={{ canvasId }} period={period} onPeriodChange={(p) => set({ from: p.from, to: p.to })} /> },
    { value: 'activity', content: <ActivityLog environmentId={environmentId} scope={{ canvasId }} /> },
  ];

  return (
    <section className="canvas-analytics">
      <p>
        <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId }}>
          ← {c.name}
        </Link>
      </p>
      <h1>
        Analytics: {c.name} <CanvasStatusBadge canvas={c} />
      </h1>
      {!launched ? (
        <EmptyState
          title="No data yet"
          description="Launch the Canvas to see analytics."
          action={
            <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId }}>
              Open the Canvas
            </Link>
          }
        />
      ) : (
        <Tabs label="Canvas analytics" value={tab} onValueChange={(v) => set({ tab: v === 'overview' ? undefined : (v as CanvasAnalyticsTab) })} items={tabs.map((t) => ({ value: t.value, label: CANVAS_TAB_LABELS[t.value], content: t.content }))} />
      )}
    </section>
  );
}
